import React, { useEffect, useState } from 'react';
import { checkHealth, getHealthEvents, createHealthEvent, updateHealthEvent, deleteHealthEvent, getMe, getHealthHistory } from './api';
import './HealthPage.css';

function HealthPage() {
  const [health, setHealth] = useState(null);
  const [events, setEvents] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  
  // Form state
  const [status, setStatus] = useState('orange');
  const [comment, setComment] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [healthRes, eventsRes, historyRes] = await Promise.all([
        checkHealth(),
        getHealthEvents(31),
        getHealthHistory()
      ]);
      setHealth(healthRes.data);
      setEvents(eventsRes.data);
      setHistory(historyRes.data);
      
      try {
        const userRes = await getMe();
        setIsAdmin(userRes.data.is_admin);
      } catch (e) {
        setIsAdmin(false);
      }
      
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      status,
      comment,
      start_time: new Date(startTime).toISOString(),
      end_time: endTime ? new Date(endTime).toISOString() : null
    };

    try {
      if (editingEvent) {
        await updateHealthEvent(editingEvent.id, payload);
      } else {
        await createHealthEvent(payload);
      }
      setShowModal(false);
      setEditingEvent(null);
      resetForm();
      fetchData();
    } catch (err) {
      alert('Failed to save event');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await deleteHealthEvent(id);
        fetchData();
      } catch (err) {
        alert('Failed to delete');
      }
    }
  };

  const resetForm = () => {
    setStatus('orange');
    setComment('');
    setStartTime('');
    setEndTime('');
  };

  const openModal = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setStatus(event.status);
      setComment(event.comment);
      setStartTime(event.start_time.slice(0, 16)); // Format for datetime-local
      setEndTime(event.end_time ? event.end_time.slice(0, 16) : '');
    } else {
      setEditingEvent(null);
      resetForm();
      // Default start time to now
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      setStartTime(now.toISOString().slice(0, 16));
    }
    setShowModal(true);
  };

  const filterEvents = (hours) => {
    const cutoff = new Date();
    cutoff.setHours(cutoff.getHours() - hours);
    return events.filter(e => new Date(e.start_time) >= cutoff);
  };

  const renderEventList = (eventList, title) => (
    <div className="health-section">
      <h3>{title}</h3>
      {eventList.length === 0 ? (
        <p className="text-muted">No incidents reported.</p>
      ) : (
        <div className="event-list">
          {eventList.map(e => (
            <div key={e.id} className="event-item" style={{ borderLeft: `4px solid ${e.status === 'red' ? 'var(--danger)' : '#f59e0b'}` }}>
              <div className="event-header">
                <span className={`badge ${e.status}`}>{e.status.toUpperCase()}</span>
                <span className="event-time">
                  {new Date(e.start_time).toLocaleString()} 
                  {e.end_time ? ` - ${new Date(e.end_time).toLocaleString()}` : ' (Ongoing)'}
                </span>
              </div>
              <p className="event-comment">{e.comment}</p>
              {isAdmin && (
                <div className="event-actions">
                  <button onClick={() => openModal(e)} className="btn-link">Edit</button>
                  <button onClick={() => handleDelete(e.id)} className="btn-link text-danger">Delete</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Graph Rendering
  const renderGraph = () => {
    if (!history || history.length === 0) return <div className="text-center text-secondary">No history data available yet.</div>;

    // --- 1. Status Grid (Availability) ---
    const gridCols = 96; // 24h * 4 (15 min blocks)
    const gridRows = 15; // 15 minutes per block
    const cellSize = 6;
    const cellGap = 2;
    const gridPadding = 20;
    
    const gridWidth = gridPadding * 2 + gridCols * (cellSize + cellGap);
    const gridHeight = gridPadding * 2 + gridRows * (cellSize + cellGap) + 10; // +10 for labels

    // Align to 15-minute buckets
    const now = new Date();
    const currentMinuteTime = Math.floor(now.getTime() / 60000) * 60000;
    
    const dataMap = new Map();
    history.forEach(h => {
        const t = Math.floor(new Date(h.timestamp).getTime() / 60000) * 60000;
        dataMap.set(t, h);
    });

    const gridCells = [];
    for (let c = 0; c < gridCols; c++) {
        for (let r = 0; r < gridRows; r++) {
            // Index 0 = Oldest (Bottom-Left), Index 1439 = Newest (Top-Right)
            const index = c * gridRows + r;
            const minutesAgo = (gridCols * gridRows) - 1 - index;
            const cellTime = currentMinuteTime - (minutesAgo * 60000);
            
            const data = dataMap.get(cellTime);
            
            let fill = '#374151'; // Default gray
            let opacity = 0.2;
            let title = new Date(cellTime).toLocaleString();

            if (data) {
                opacity = 1;
                if (data.status === 'up' || data.status === 'ok') fill = '#10b981';
                else if (data.status === 'affected') fill = '#f59e0b';
                else if (data.status === 'down') fill = '#ef4444';
                title += ` - ${data.status.toUpperCase()} (${Math.round(data.ping_ms)}ms)`;
            }

            const x = gridPadding + c * (cellSize + cellGap);
            const y = gridHeight - gridPadding - 15 - (r + 1) * (cellSize + cellGap); // -15 for labels space

            gridCells.push(
                <rect key={`g-${c}-${r}`} x={x} y={y} width={cellSize} height={cellSize} fill={fill} opacity={opacity} rx={1}>
                    <title>{title}</title>
                </rect>
            );
        }
    }
    
    // Grid Labels
    const gridLabels = [];
    for (let c = 0; c < gridCols; c += 4) { // Every hour
        if (c % 8 === 0) { // Label every 2 hours
            const x = gridPadding + c * (cellSize + cellGap);
            const minutesAgo = (gridCols * gridRows) - 1 - (c * gridRows);
            const labelTime = new Date(currentMinuteTime - minutesAgo * 60000);
            gridLabels.push(
                <text key={`gl-${c}`} x={x} y={gridHeight - 5} fontSize="9" fill="var(--text-secondary)">
                    {labelTime.getHours()}:00
                </text>
            );
        }
    }

    // --- 2. Ping Line Graph ---
    const lineHeight = 250;
    const lineWidth = 800;
    const linePadding = 40;
    
    const maxPingData = Math.max(...history.map(h => h.ping_ms), 10);
    let yStep = 20;
    if (maxPingData > 100) yStep = 50;
    if (maxPingData > 500) yStep = 100;
    const maxPing = Math.ceil(maxPingData / yStep) * yStep;

    const minTime = new Date(history[0].timestamp).getTime();
    const maxTime = new Date(history[history.length - 1].timestamp).getTime();
    const timeRange = maxTime - minTime || 1;

    const getLineX = (timestamp) => {
      const t = new Date(timestamp).getTime();
      return linePadding + ((t - minTime) / timeRange) * (lineWidth - 2 * linePadding);
    };

    const getLineY = (ping) => {
      return lineHeight - linePadding - (ping / maxPing) * (lineHeight - 2 * linePadding);
    };

    let pathD = `M ${getLineX(history[0].timestamp)} ${getLineY(history[0].ping_ms)}`;
    history.forEach((h, i) => {
      if (i === 0) return;
      pathD += ` L ${getLineX(h.timestamp)} ${getLineY(h.ping_ms)}`;
    });
    const areaD = `${pathD} L ${getLineX(history[history.length-1].timestamp)} ${lineHeight-linePadding} L ${getLineX(history[0].timestamp)} ${lineHeight-linePadding} Z`;

    // Line Graph Axes
    const lineYAxis = [];
    for (let val = 0; val <= maxPing; val += yStep) {
        lineYAxis.push(
            <g key={`ly-${val}`}>
                <line x1={linePadding} y1={getLineY(val)} x2={lineWidth-linePadding} y2={getLineY(val)} stroke="var(--border)" strokeDasharray="4" opacity="0.5" />
                <text x={linePadding - 5} y={getLineY(val) + 4} fontSize="10" fill="var(--text-secondary)" textAnchor="end">{val}</text>
            </g>
        );
    }
    const lineXAxis = [];
    const timeStep = timeRange / 6;
    for (let t = minTime; t <= maxTime; t += timeStep) {
        lineXAxis.push(
            <g key={`lx-${t}`}>
                <line x1={getLineX(t)} y1={linePadding} x2={getLineX(t)} y2={lineHeight-linePadding} stroke="var(--border)" strokeDasharray="4" opacity="0.3" />
                <text x={getLineX(t)} y={lineHeight - linePadding + 15} fontSize="10" fill="var(--text-secondary)" textAnchor="middle">
                    {new Date(t).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </text>
            </g>
        );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        {/* Status Grid */}
        <div style={{ width: '100%', overflowX: 'auto', background: 'var(--bg-surface)', borderRadius: 8, padding: '1rem', border: '1px solid var(--border)' }}>
            <h4 style={{fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1rem'}}>Availability Map (15m columns)</h4>
            <svg width={gridWidth} height={gridHeight} style={{ minWidth: gridWidth }}>
                {gridCells}
                {gridLabels}
            </svg>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '0.5rem', fontSize: '0.8rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 10, height: 10, background: '#10b981' }}></div> Up</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 10, height: 10, background: '#f59e0b' }}></div> Affected</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 10, height: 10, background: '#ef4444' }}></div> Down</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 10, height: 10, background: '#374151', opacity: 0.3 }}></div> No Data</div>
            </div>
        </div>

        {/* Ping Graph */}
        <div style={{ width: '100%', overflowX: 'auto', background: 'var(--bg-surface)', borderRadius: 8, padding: '1rem', border: '1px solid var(--border)' }}>
            <h4 style={{fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1rem'}}>Latency History</h4>
            <svg viewBox={`0 0 ${lineWidth} ${lineHeight}`} style={{ width: '100%', minWidth: 600, height: 'auto' }}>
                {lineYAxis}
                {lineXAxis}
                <path d={areaD} fill="var(--primary)" fillOpacity="0.1" />
                <path d={pathD} fill="none" stroke="var(--primary)" strokeWidth="2" />
                <line x1={linePadding} y1={lineHeight-linePadding} x2={lineWidth-linePadding} y2={lineHeight-linePadding} stroke="var(--text-secondary)" strokeWidth="1" />
                <line x1={linePadding} y1={linePadding} x2={linePadding} y2={lineHeight-linePadding} stroke="var(--text-secondary)" strokeWidth="1" />
            </svg>
        </div>
      </div>
    );
  };

  return (
    <div className="container health-page">
      <div className="header-actions">
        <h2>System Health</h2>
        <div>
            {isAdmin && <button onClick={() => openModal()} className="btn btn-primary" style={{marginRight: '10px'}}>+ Report Incident</button>}
            <button onClick={fetchData} className="btn btn-secondary">Refresh</button>
        </div>
      </div>

      {/* Current Status Summary */}
      <div className="card mb-4">
        <h3>Current Status</h3>
        <div className="status-grid">
            <div className="status-item">
                <span>API</span>
                <span className={`status-badge ${health?.status === 'ok' ? 'success' : 'danger'}`}>{health?.status || 'Unknown'}</span>
            </div>
            <div className="status-item">
                <span>Database</span>
                <span className={`status-badge ${health?.database === 'connected' ? 'success' : 'danger'}`}>{health?.database || 'Unknown'}</span>
            </div>
            <div className="status-item">
                <span>Storage</span>
                <span className={`status-badge ${health?.storage === 'accessible' ? 'success' : 'danger'}`}>{health?.storage || 'Unknown'}</span>
            </div>
        </div>
      </div>
      {/* History Graph */}
      <div className="card mb-4">
        <h3>24h Performance</h3>
        {renderGraph()}
      </div>
      {renderEventList(filterEvents(24), "Past 24 Hours")}
      {renderEventList(filterEvents(24 * 7), "Past 7 Days")}
      {renderEventList(filterEvents(24 * 31), "Past 31 Days")}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{editingEvent ? 'Edit Incident' : 'Report Incident'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Status</label>
                <select value={status} onChange={e => setStatus(e.target.value)} className="form-control">
                  <option value="orange">Orange (Degraded)</option>
                  <option value="red">Red (Outage)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Comment</label>
                <textarea value={comment} onChange={e => setComment(e.target.value)} className="form-control" required />
              </div>
              <div className="form-group">
                <label>Start Time</label>
                <input type="datetime-local" value={startTime} onChange={e => setStartTime(e.target.value)} className="form-control" required />
              </div>
              <div className="form-group">
                <label>End Time (Optional)</label>
                <input type="datetime-local" value={endTime} onChange={e => setEndTime(e.target.value)} className="form-control" />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default HealthPage;
